"use client"

import { useState, useRef, useEffect } from "react"
import {
  FileIcon,
  FileText,
  ImageIcon,
  ArrowLeft,
  CheckCheck,
  Reply,
  X,
  CornerUpLeft,
  Trash2,
  MoreVertical,
  Smile,
  Send,
  User,
  Info,
  Users,
  MessageSquare,
} from "lucide-react"
import { getMessages } from "./api"
import { postStudentChatFile, generatePresignedUrl } from "../postFileAws"

// Lambda API URL for backend operations
const LAMBDA_API_URL = "https://tvaf32pm2tarajmrxaxyy3pp3e0zgjyx.lambda-url.ap-south-1.on.aws/"

// CSS styles for chat area (including new mention styles)
const chatAreaStyles = `
  /* Existing styles... */
  .reaction-tooltip {
    position: relative;
    display: inline-block;
  }

  .reaction-tooltip .tooltip-content {
    visibility: hidden;
    background-color: rgba(0, 0, 0, 0.9);
    color: white;
    text-align: center;
    border-radius: 6px;
    padding: 8px 12px;
    position: absolute;
    z-index: 30;
    bottom: 125%;
    left: 50%;
    transform: translateX(-50%);
    opacity: 0;
    transition: opacity 0.3s, visibility 0.3s;
    white-space: nowrap;
    font-size: 0.75rem;
    pointer-events: none;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
    min-width: max-content;
  }

  .reaction-tooltip:hover .tooltip-content {
    visibility: visible !important;
    opacity: 1 !important;
  }

  .reaction-tooltip .tooltip-content::after {
    content: "";
    position: absolute;
    top: 100%;
    left: 50%;
    margin-left: -5px;
    border-width: 5px;
    border-style: solid;
    border-color: rgba(0, 0, 0, 0.9) transparent transparent transparent;
  }

  /* Enhanced reaction badge styles */
  .reaction-badge {
    background-color: rgba(59, 130, 246, 0.1);
    border-radius: 14px;
    padding: 4px 10px;
    font-size: 0.8rem;
    display: flex;
    align-items: center;
    gap: 4px;
    min-width: 36px;
    min-height: 24px;
    justify-content: center;
    white-space: nowrap;
    flex-shrink: 0;
    border: 1px solid transparent;
    transition: all 0.2s ease;
    position: relative;
    z-index: 6;
    cursor: pointer;
  }

  .reaction-badge.active {
    background-color: rgba(59, 130, 246, 0.2);
    border: 1px solid rgba(59, 130, 246, 0.3);
  }

  .reaction-badge:hover {
    background-color: rgba(59, 130, 246, 0.15);
    transform: scale(1.05);
  }

  /* Message reactions container */
  .message-reactions {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 8px;
    min-height: 28px;
    width: 100%;
    align-items: center;
    position: relative;
    z-index: 5;
    clear: both;
  }

  /* Enhanced message bubble */
  .message-bubble {
    position: relative;
    min-width: 140px;
    word-wrap: break-word;
    overflow-wrap: break-word;
    max-width: 85%;
    display: flex;
    flex-direction: column;
    z-index: 1;
  }

  .message-content {
    margin-bottom: 6px;
    line-height: 1.4;
    position: relative;
    z-index: 2;
  }

  .message-timestamp {
    margin-top: 4px;
    position: relative;
    z-index: 4;
  }

  /* Reply styles */
  .reply-container {
    background-color: rgba(0, 0, 0, 0.05);
    border-left: 3px solid #3b82f6;
    padding: 8px;
    margin-bottom: 8px;
    border-radius: 4px;
  }

  .reply-header {
    display: flex;
    align-items: center;
    margin-bottom: 4px;
  }

  .reply-content {
    font-size: 0.875rem;
    color: #4b5563;
    word-break: break-word;
    white-space: normal;
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }

  .message-actions {
    position: absolute;
    top: -10px;
    right: 10px;
    background-color: white;
    border-radius: 50%;
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    opacity: 0;
    transition: opacity 0.2s ease;
  }

  .message-bubble:hover .message-actions {
    opacity: 1;
  }

  .message-selection-menu {
    position: absolute;
    top: -45px;
    right: 10px;
    background-color: white;
    border-radius: 8px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
    display: flex;
    padding: 4px;
    z-index: 10;
  }

  .message-selection-menu button {
    padding: 6px;
    border-radius: 50%;
    margin: 0 2px;
    transition: background-color 0.2s;
  }

  .message-selection-menu button:hover {
    background-color: #f3f4f6;
  }

  .reaction-menu {
    position: absolute;
    top: -45px;
    right: 10px;
    background-color: white;
    border-radius: 20px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
    display: flex;
    padding: 6px;
    z-index: 20;
  }

  .reaction-menu button {
    font-size: 1.2rem;
    padding: 4px 6px;
    border-radius: 12px;
    margin: 0 2px;
    transition: transform 0.2s;
  }

  .reaction-menu button:hover {
    transform: scale(1.2);
    background-color: #f3f4f6;
  }

  /* NEW: Mention dropdown styles */
  .mention-dropdown {
    position: absolute;
    bottom: 100%;
    left: 0;
    right: 0;
    background-color: white;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.1);
    max-height: 200px;
    overflow-y: auto;
    z-index: 50;
    margin-bottom: 8px;
  }

  .mention-item {
    display: flex;
    align-items: center;
    padding: 12px 16px;
    cursor: pointer;
    transition: background-color 0.2s;
    border-bottom: 1px solid #f3f4f6;
  }

  .mention-item:last-child {
    border-bottom: none;
  }

  .mention-item:hover,
  .mention-item.selected {
    background-color: #f3f4f6;
  }

  .mention-item.selected {
    background-color: #dbeafe;
  }

  .mention-avatar {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    margin-right: 12px;
    background-color: #f3f4f6;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  .mention-name {
    font-weight: 500;
    color: #374151;
    font-size: 0.875rem;
  }

  .mention-tag {
    background-color: #dbeafe;
    color: #1d4ed8;
    padding: 2px 6px;
    border-radius: 4px;
    font-size: 0.75rem;
    font-weight: 500;
    margin-left: 8px;
  }

  /* Typing animation */
  .typing-indicator {
    min-height: 20px;
    padding: 4px 0;
  }

  .typing-animation {
    display: inline-block;
  }

  .typing-animation .dot {
    display: inline-block;
    animation: typingDot 1.4s infinite;
    animation-fill-mode: both;
  }

  .typing-animation .dot:nth-child(2) {
    animation-delay: 0.2s;
  }

  .typing-animation .dot:nth-child(3) {
    animation-delay: 0.4s;
  }

  @keyframes typingDot {
    0% { opacity: 0.2; }
    20% { opacity: 1; }
    100% { opacity: 0.2; }
  }

  /* Mobile selection styles */
  .mobile-selection-header {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    background-color: #3b82f6;
    color: white;
    padding: 12px 16px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    z-index: 1000;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  .mobile-selection-actions {
    display: flex;
    align-items: center;
    gap: 16px;
  }

  .mobile-selection-emoji-bar {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    background-color: white;
    padding: 16px;
    display: flex;
    justify-content: space-around;
    align-items: center;
    border-top: 1px solid #e5e7eb;
    z-index: 1000;
    box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.1);
    min-height: 80px;
  }

  .mobile-selection-emoji-bar button {
    font-size: 1.8rem;
    padding: 12px;
    border-radius: 12px;
    transition: all 0.2s;
    background: transparent;
    border: none;
    min-width: 48px;
    min-height: 48px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .mobile-selection-emoji-bar button:active {
    transform: scale(1.2);
    background-color: #f3f4f6;
  }

  .mobile-chat-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.3);
    z-index: 999;
  }

  .message-selected {
    background-color: rgba(59, 130, 246, 0.2) !important;
    border: 2px solid #3b82f6 !important;
  }

  @media (max-width: 768px) {
    .chat-header {
      margin-top: 0 !important;
    }

    .mobile-selection-mode .chat-header {
      margin-top: 60px !important;
    }

    .mobile-selection-mode .chat-messages {
      padding-bottom: 100px !important;
    }

    .mention-dropdown {
      max-height: 150px;
    }

    .mention-item {
      padding: 10px 12px;
    }

    .mention-avatar {
      width: 28px;
      height: 28px;
      margin-right: 10px;
    }
  }

  /* Reaction details modal styles */
  .reaction-details-modal {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 50;
  }

  .reaction-details-content {
    background-color: white;
    border-radius: 8px;
    padding: 16px;
    max-width: 300px;
    width: 90%;
    max-height: 80vh;
    overflow-y: auto;
  }

  .reaction-details-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
    padding-bottom: 8px;
    border-bottom: 1px solid #e5e7eb;
  }

  .reaction-user-list {
    margin: 0;
    padding: 0;
    list-style: none;
  }

  .reaction-user-item {
    padding: 8px 0;
    border-bottom: 1px solid #f3f4f6;
  }

  .reaction-user-item:last-child {
    border-bottom: none;
  }

  @media (max-width: 768px) {
    .mobile-chat-view {
      padding-bottom: 0 !important;
      margin-bottom: 0 !important;
    }
    
    .mobile-chat-view form {
      padding-bottom: 0 !important;
      margin-bottom: 0 !important;
    }
  }
`

const ChatArea = ({
  currentChatId,
  setCurrentChatId,
  conversations,
  setConversations,
  currentUser,
  ws,
  isMobile,
  isMobileChatOpen,
  setIsMobileChatOpen,
  profileImageUrls,
  isUserOnline,
  typingStatus,
  setTypingStatus,
  getChatName,
  setSeenViaAPI,
  handleProfileClick,
  handleGroupInfoClick,
  formatMessageTimeForDisplay,
}) => {
  // Message and chat state
  const [currentMessageArray, setCurrentMessageArray] = useState([])
  const [message, setMessage] = useState("")
  const [file, setFile] = useState(null)
  const [selectedFileName, setSelectedFileName] = useState(null)
  const [isLoadingMessages, setIsLoadingMessages] = useState(false)

  // Reply functionality
  const [replyTo, setReplyTo] = useState(null)

  // Reaction functionality
  const [showReactionDetails, setShowReactionDetails] = useState(false)
  const [selectedReaction, setSelectedReaction] = useState({ emoji: "", users: [] })

  // Typing functionality
  const [isTyping, setIsTyping] = useState(false)
  const typingTimeoutRef = useRef(null)

  // Message loading and pagination
  const [isLoadingMoreMessages, setIsLoadingMoreMessages] = useState(false)
  const [hasMoreMessages, setHasMoreMessages] = useState(true)
  const [oldestMessageTimestamp, setOldestMessageTimestamp] = useState(null)

  // Mobile selection state
  const [longPressTimer, setLongPressTimer] = useState(null)
  const [selectedMessages, setSelectedMessages] = useState([])
  const [isSelectionMode, setIsSelectionMode] = useState(false)
  const [showMobileEmojiBar, setShowMobileEmojiBar] = useState(false)
  const [selectedMessage, setSelectedMessage] = useState(null)
  const [showReactionMenu, setShowReactionMenu] = useState(false)

  // NEW: Mention functionality state
  const [showMentionDropdown, setShowMentionDropdown] = useState(false)
  const [mentionQuery, setMentionQuery] = useState("")
  const [mentionStartIndex, setMentionStartIndex] = useState(-1)
  const [selectedMentionIndex, setSelectedMentionIndex] = useState(0)
  const [filteredParticipants, setFilteredParticipants] = useState([])

  // Refs
  const messagesEndRef = useRef(null)
  const messagesContainerRef = useRef(null)
  const fileInputRef = useRef(null)
  const messageInputRef = useRef(null) // NEW: Ref for message input

  // NEW: Get current chat participants for mentions
  const getCurrentChatParticipants = () => {
    const currentChat = conversations.find((c) => c._id === currentChatId)
    if (!currentChat || !currentChat.participants) return []

    // Filter out current user from mentions
    return currentChat.participants.filter((p) => p.pid !== currentUser?.pid)
  }

  // NEW: Filter participants based on mention query
  const filterParticipants = (query) => {
    const participants = getCurrentChatParticipants()
    if (!query) return participants

    return participants.filter((participant) => participant.pName.toLowerCase().includes(query.toLowerCase()))
  }

  // NEW: Handle mention detection and filtering
  const handleMentionDetection = (inputValue, cursorPosition) => {
    const textBeforeCursor = inputValue.substring(0, cursorPosition)
    const lastAtIndex = textBeforeCursor.lastIndexOf("@")

    if (lastAtIndex === -1) {
      setShowMentionDropdown(false)
      return
    }

    // Check if there's a space between @ and cursor (which would break the mention)
    const textAfterAt = textBeforeCursor.substring(lastAtIndex + 1)
    if (textAfterAt.includes(" ")) {
      setShowMentionDropdown(false)
      return
    }

    // Only show mentions for group chats
    const currentChat = conversations.find((c) => c._id === currentChatId)
    if (!currentChat || currentChat.chatType !== "pTe") {
      setShowMentionDropdown(false)
      return
    }

    const query = textAfterAt
    const filtered = filterParticipants(query)

    setMentionQuery(query)
    setMentionStartIndex(lastAtIndex)
    setFilteredParticipants(filtered)
    setSelectedMentionIndex(0)
    setShowMentionDropdown(filtered.length > 0)
  }

  // NEW: Handle mention selection
  const selectMention = (participant) => {
    if (mentionStartIndex === -1) return

    const beforeMention = message.substring(0, mentionStartIndex)
    const afterMention = message.substring(mentionStartIndex + mentionQuery.length + 1) // +1 for @
    const mentionText = `@${participant.pName} `

    const newMessage = beforeMention + mentionText + afterMention
    setMessage(newMessage)
    setShowMentionDropdown(false)

    // Focus back to input and set cursor position
    setTimeout(() => {
      if (messageInputRef.current) {
        const newCursorPosition = beforeMention.length + mentionText.length
        messageInputRef.current.focus()
        messageInputRef.current.setSelectionRange(newCursorPosition, newCursorPosition)
      }
    }, 0)
  }

  // NEW: Handle keyboard navigation in mention dropdown
  const handleMentionKeyDown = (e) => {
    if (!showMentionDropdown) return

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault()
        setSelectedMentionIndex((prev) => (prev < filteredParticipants.length - 1 ? prev + 1 : 0))
        break
      case "ArrowUp":
        e.preventDefault()
        setSelectedMentionIndex((prev) => (prev > 0 ? prev - 1 : filteredParticipants.length - 1))
        break
      case "Enter":
        if (filteredParticipants[selectedMentionIndex]) {
          e.preventDefault()
          selectMention(filteredParticipants[selectedMentionIndex])
        }
        break
      case "Escape":
        setShowMentionDropdown(false)
        break
    }
  }

  const getFileIcon = (fileName) => {
    const extension = fileName.split(".").pop()?.toLowerCase() || ""
    if (["jpg", "jpeg", "png", "gif"].includes(extension)) {
      return <ImageIcon className="w-6 h-6 text-green-500" />
    } else if (extension === "pdf") {
      return <FileText className="w-6 h-6 text-red-500" />
    } else {
      return <FileIcon className="w-6 h-6 text-gray-500" />
    }
  }

  // Scroll to bottom function - only used when opening a chat
  const scrollToBottom = () => {
    if (messagesContainerRef.current) {
      const container = messagesContainerRef.current
      container.scrollTop = container.scrollHeight
    }

    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({
        behavior: "auto",
        block: "end",
        inline: "nearest",
      })
    }
  }

  // Fetch messages function
  const fetchMessages = async (chatId, before = null) => {
    try {
      const response = await getMessages(chatId, before)
      if (!response) {
        throw new Error("Failed to fetch messages")
      }

      if (response.messages && response.hasMore !== undefined) {
        setHasMoreMessages(response.hasMore)
        setOldestMessageTimestamp(response.oldestTimestamp)

        const sortedMessages = response.messages.sort(
          (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
        )

        return sortedMessages
      } else {
        setHasMoreMessages(false)

        const sortedMessages = response.sort(
          (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
        )

        return sortedMessages
      }
    } catch (error) {
      console.error("Error fetching messages:", error)
      return []
    }
  }

  // Load messages when chat changes
  useEffect(() => {
    if (currentChatId) {
      setIsLoadingMessages(true)
      setOldestMessageTimestamp(null)
      setHasMoreMessages(true)

      fetchMessages(currentChatId).then((messages) => {
        setCurrentMessageArray(messages)
        setIsLoadingMessages(false)

        // Scroll to bottom when opening a chat to show latest messages
        setTimeout(() => {
          scrollToBottom()
        }, 100)

        if (currentUser) {
          setSeenViaAPI(currentChatId, currentUser.pid)
        }
      })

      // Clear any active reply when changing chats
      setReplyTo(null)
      clearMessageSelection()
      // NEW: Clear mention dropdown when changing chats
      setShowMentionDropdown(false)
    }
  }, [currentChatId])

  // Load more messages function
  const loadMoreMessages = async () => {
    if (!hasMoreMessages || isLoadingMoreMessages || !currentChatId || !oldestMessageTimestamp) {
      return
    }

    setIsLoadingMoreMessages(true)

    try {
      const container = messagesContainerRef.current
      if (!container) return

      const scrollHeight = container.scrollHeight

      const olderMessages = await fetchMessages(currentChatId, oldestMessageTimestamp)

      if (olderMessages.length > 0) {
        setCurrentMessageArray((prevMessages) => [...olderMessages, ...prevMessages])

        setTimeout(() => {
          const newScrollHeight = container.scrollHeight
          container.scrollTop = newScrollHeight - scrollHeight
        }, 100)
      }
    } catch (error) {
      console.error("Error loading more messages:", error)
    } finally {
      setIsLoadingMoreMessages(false)
    }
  }

  // Scroll event handler for loading more messages
  useEffect(() => {
    const container = messagesContainerRef.current
    if (!container) return

    const handleScroll = () => {
      if (container.scrollTop < 100 && hasMoreMessages && !isLoadingMoreMessages) {
        loadMoreMessages()
      }
    }

    container.addEventListener("scroll", handleScroll)
    return () => container.removeEventListener("scroll", handleScroll)
  }, [hasMoreMessages, isLoadingMoreMessages, currentChatId, oldestMessageTimestamp])

  // MODIFIED: Handle typing with mention detection
  const handleTyping = (e) => {
    const value = e.target.value
    const cursorPosition = e.target.selectionStart

    setMessage(value)

    // NEW: Handle mention detection
    handleMentionDetection(value, cursorPosition)

    if (!ws || !currentChatId) return

    if (!isTyping) {
      setIsTyping(true)

      ws.send(
        JSON.stringify({
          action: "setTyping",
          gid: currentChatId,
          pid: currentUser?.pid,
          pName: currentUser?.pName,
          isTyping: true,
        }),
      )
    }

    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current)
    }

    typingTimeoutRef.current = setTimeout(() => {
      setIsTyping(false)

      ws.send(
        JSON.stringify({
          action: "setTyping",
          gid: currentChatId,
          pid: currentUser?.pid,
          pName: currentUser?.pName,
          isTyping: false,
        }),
      )
    }, 2000)
  }

  // Cleanup for typing status when changing chats
  useEffect(() => {
    if (currentChatId) {
      setIsTyping(false)
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current)
      }

      if (isTyping && ws) {
        ws.send(
          JSON.stringify({
            action: "setTyping",
            gid: currentChatId,
            pid: currentUser?.pid,
            pName: currentUser?.pName,
            isTyping: false,
          }),
        )
      }

      setReplyTo(null)
      clearMessageSelection()
    }
  }, [currentChatId])

  // Cleanup when component unmounts
  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current)
      }

      if (isTyping && ws && currentChatId) {
        ws.send(
          JSON.stringify({
            action: "setTyping",
            gid: currentChatId,
            pid: currentUser?.pid,
            pName: currentUser?.pName,
            isTyping: false,
          }),
        )
      }
    }
  }, [isTyping, ws, currentChatId, currentUser])

  const handleFileChange = (e) => {
    const selectedFile = e.target.files?.[0]
    if (selectedFile) {
      const fileType = selectedFile.type
      const validTypes = ["application/pdf", "image/jpeg", "image/png", "image/gif"]
      if (validTypes.includes(fileType)) {
        setFile(selectedFile)
        setSelectedFileName(selectedFile.name)
        // Don't clear the message text so it can be used as a caption
      } else {
        alert("Please select a PDF or image file.")
        e.target.value = ""
      }
    }
  }

  // Function to handle replying to a message
  const handleReplyToMessage = (message) => {
    setReplyTo({
      messageId: message._id.toString(),
      pid: message.pid,
      pName: message.pName,
      message: message.message,
      messageType: message.messageType,
    })
  }

  // Enhanced mobile selection functions
  const clearMessageSelection = () => {
    setIsSelectionMode(false)
    setSelectedMessages([])
    setSelectedMessage(null)
    setShowReactionMenu(false)
    setShowMobileEmojiBar(false)
  }

  // Function to toggle reaction menu
  const toggleReactionMenu = (e) => {
    e.stopPropagation()
    setShowReactionMenu(!showReactionMenu)
  }

  // FIXED: Function to add reaction to a message
  const addReaction = async (emoji, messageId) => {
    if (!ws || !currentChatId || !currentUser) return

    try {
      // Send reaction via WebSocket
      ws.send(
        JSON.stringify({
          action: "sendMessage",
          gid: currentChatId,
          pid: currentUser.pid,
          pName: currentUser.pName,
          messageType: "reaction",
          targetMessageId: messageId,
          reaction: emoji,
        }),
      )

      // DON'T update local state here - let the WebSocket response handle it
      // This prevents the "comes and goes" issue
      clearMessageSelection()
    } catch (error) {
      console.error("Error adding reaction:", error)
    }
  }

  // Enhanced mobile reaction function
  const addReactionToSelected = async (emoji) => {
    if (selectedMessages.length === 0) return

    for (const messageId of selectedMessages) {
      await addReaction(emoji, messageId)
    }

    clearMessageSelection()
  }

  // Function to delete a message via the Lambda API
  const deleteMessage = async (messageId) => {
    if (!currentUser?.pid || !currentChatId) return

    try {
      const response = await fetch(LAMBDA_API_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          request: "deleteMessage",
          body: {
            pid: currentUser.pid,
            messageId: messageId,
            gid: currentChatId,
          },
        }),
      })

      if (!response.ok) {
        throw new Error(`API call failed with status: ${response.status}`)
      }

      const data = await response.json()
      console.log("deleteMessage API response:", data)

      setCurrentMessageArray((prevMessages) => {
        return prevMessages.map((msg) => {
          if (msg._id === messageId) {
            return {
              ...msg,
              isDeleted: true,
              message: "This message was deleted",
            }
          }
          return msg
        })
      })

      if (ws) {
        ws.send(
          JSON.stringify({
            action: "sendMessage",
            gid: currentChatId,
            pid: currentUser.pid,
            pName: currentUser.pName,
            messageType: "delete",
            deletedMessageId: messageId,
          }),
        )
      }

      return data
    } catch (error) {
      console.error("Error calling deleteMessage API:", error)
      return null
    }
  }

  // Enhanced delete function for mobile
  const deleteSelectedMessages = async () => {
    if (selectedMessages.length === 0) return

    const confirmMessage =
      selectedMessages.length === 1 ? "Delete this message?" : `Delete ${selectedMessages.length} messages?`

    if (window.confirm(confirmMessage)) {
      for (const messageId of selectedMessages) {
        await deleteMessage(messageId)
      }
      clearMessageSelection()
    }
  }

  // Updated renderReactions function with enhanced tooltip and click handling
  const renderReactions = (reactions, messageId) => {
    if (!reactions || reactions.length === 0) return null

    const reactionCounts = {}

    reactions.forEach((reaction) => {
      if (!reactionCounts[reaction.reaction]) {
        reactionCounts[reaction.reaction] = {
          count: 0,
          users: [],
          hasCurrentUser: false,
        }
      }
      reactionCounts[reaction.reaction].count += 1
      const userName = reaction.pname || reaction.pName
      reactionCounts[reaction.reaction].users.push(userName)

      if (reaction.pid === currentUser?.pid) {
        reactionCounts[reaction.reaction].hasCurrentUser = true
      }
    })

    return (
      <div className="message-reactions">
        {Object.entries(reactionCounts).map(([emoji, data]) => (
          <div key={`${messageId}-${emoji}`} className="reaction-tooltip">
            <button
              className={`reaction-badge ${data.hasCurrentUser ? "active" : ""}`}
              onClick={(e) => {
                e.stopPropagation()
                if (e.detail === 2) {
                  setSelectedReaction({ emoji, users: data.users })
                  setShowReactionDetails(true)
                } else {
                  addReaction(emoji, messageId)
                }
              }}
            >
              {emoji} {data.count}
            </button>
            <span className="tooltip-content">{data.users.join(", ")}</span>
          </div>
        ))}
      </div>
    )
  }

  // Reaction details modal component
  const ReactionDetailsModal = () => {
    if (!showReactionDetails) return null

    return (
      <div className="reaction-details-modal" onClick={() => setShowReactionDetails(false)}>
        <div className="reaction-details-content" onClick={(e) => e.stopPropagation()}>
          <div className="reaction-details-header">
            <h3 className="text-lg font-medium">
              {selectedReaction.emoji} Reactions ({selectedReaction.users.length})
            </h3>
            <button onClick={() => setShowReactionDetails(false)} className="text-gray-500 hover:text-gray-700">
              <X size={18} />
            </button>
          </div>
          <ul className="reaction-user-list">
            {selectedReaction.users.map((user, index) => (
              <li key={index} className="reaction-user-item">
                {user}
              </li>
            ))}
          </ul>
        </div>
      </div>
    )
  }

  // NEW: Mention dropdown component
  const MentionDropdown = () => {
    if (!showMentionDropdown || filteredParticipants.length === 0) return null

    return (
      <div className="mention-dropdown">
        {filteredParticipants.map((participant, index) => (
          <div
            key={participant.pid}
            className={`mention-item ${index === selectedMentionIndex ? "selected" : ""}`}
            onClick={() => selectMention(participant)}
          >
            <div className="mention-avatar">
              {profileImageUrls[participant.pid] ? (
                <img
                  src={profileImageUrls[participant.pid] || "/placeholder.svg"}
                  alt={participant.pName}
                  className="w-full h-full rounded-full object-cover"
                />
              ) : (
                <User className="w-4 h-4 text-gray-500" />
              )}
            </div>
            <div className="mention-name">{participant.pName}</div>
            {participant.pid === currentUser?.pid && <div className="mention-tag">You</div>}
          </div>
        ))}
      </div>
    )
  }

  const renderChatHeader = () => {
    const currentChat = conversations.find((c) => c._id === currentChatId)
    if (!currentChat) return <div className="w-14 h-14 bg-gray-200 rounded-full" />

    if (currentChat.chatType === "pTp") {
      const other = currentChat.participants.find((p) => p.pid !== currentUser?.pid)
      const isOnline = isUserOnline(other?.pid)

      return (
        <div
          className="flex items-center chat-header-profile-clickable"
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            console.log("Profile clicked:", other)
            if (other) {
              handleProfileClick({
                ...other,
                profileImageUrl: profileImageUrls[other.pid] || null,
              })
            }
          }}
          style={{ cursor: "pointer" }}
        >
          <div className="relative mr-4">
            {other?.profilePhotoS3Uri && profileImageUrls[other.pid] ? (
              isOnline ? (
                <div className="gradient-border-avatar">
                  <img
                    src={profileImageUrls[other.pid] || "/placeholder.svg"}
                    alt={getChatName(currentChat)}
                    className="w-14 h-14 object-cover"
                  />
                </div>
              ) : (
                <img
                  src={profileImageUrls[other.pid] || "/placeholder.svg"}
                  alt={getChatName(currentChat)}
                  className="w-14 h-14 rounded-full object-cover"
                />
              )
            ) : (
              <div className="w-14 h-14 rounded-full bg-gray-200 flex items-center justify-center">
                <User className="w-6 h-6 text-gray-500" />
              </div>
            )}
          </div>

          <div>
            <h2 className="text-xl font-bold">{getChatName(currentChat)}</h2>
            <p className={isOnline ? "text-green-600 text-sm font-medium" : "text-gray-400 text-sm"}>
              {isOnline ? "Online" : "Offline"}
            </p>
          </div>
        </div>
      )
    } else if (currentChat.chatType === "pTe") {
      return (
        <div
          className="flex items-center chat-header-profile-clickable"
          onClick={(e) => {
            e.stopPropagation()
            handleGroupInfoClick(currentChat)
          }}
        >
          <div className="w-14 h-14 rounded-full mr-4 bg-gray-200 flex items-center justify-center">
            <Users className="h-6 w-6 text-gray-500" />
          </div>
          <div>
            <h2 className="text-xl font-bold">{currentChat.chatName || "Group Chat"}</h2>
            <p className="text-gray-400 text-sm">{currentChat.participants?.length || 0} members</p>
          </div>
        </div>
      )
    }
  }

  const renderGroupInfoButton = () => {
    const currentChat = conversations.find((c) => c._id === currentChatId)
    return currentChat?.chatType === "pTe" ? (
      <button onClick={() => handleGroupInfoClick(currentChat)} className="group-info-button" title="Group Info">
        <Info size={20} className="text-gray-600" />
      </button>
    ) : null
  }

  const renderTypingIndicator = () => {
    if (!currentChatId || !typingStatus[currentChatId]) return null
    const typingUsers = Object.values(typingStatus[currentChatId]).filter((u) => u?.isTyping)
    if (typingUsers.length === 0) return null
    return (
      <div className="text-xs text-gray-500 italic flex items-center">
        <span>{formatTypingIndicator(typingUsers)}</span>
        <span className="typing-animation ml-1">
          <span className="dot">.</span>
          <span className="dot">.</span>
          <span className="dot">.</span>
        </span>
      </div>
    )
  }

  const renderMessageStatus = (message, messageIndex) => {
    if (message.pid !== currentUser?.pid) return null

    const currentChat = conversations.find((c) => c._id === currentChatId)
    if (!currentChat) return null

    const recipients = currentChat.participants.filter((p) => p.pid !== currentUser.pid)

    // For new/temporary conversations, show grey checkmarks initially
    if (currentChatId.startsWith("temp_") || message.isLocalMessage) {
      return <CheckCheck size={14} className="text-gray-400" />
    }

    const maxUnseenCounter = Math.max(...recipients.map((p) => p.unseenCounter || 0))

    if (maxUnseenCounter === 0) {
      return <CheckCheck size={14} className="text-blue-500" />
    }

    const totalMessages = currentMessageArray.length
    const userMessages = currentMessageArray.filter((msg) => msg.pid === currentUser.pid)
    const currentMessageIndexInUserMessages = userMessages.findIndex((msg) => msg._id === message._id)
    const userMessageFromEnd = userMessages.length - currentMessageIndexInUserMessages - 1

    if (userMessageFromEnd < maxUnseenCounter) {
      return <CheckCheck size={14} className="text-gray-400" />
    } else {
      return <CheckCheck size={14} className="text-blue-500" />
    }
  }

  const renderGroupedMessages = () => {
    const messagesByDate = {}

    currentMessageArray.forEach((msg, index) => {
      const msgDate = new Date(msg.createdAt)
      const key = `${msgDate.getFullYear()}-${msgDate.getMonth()}-${msgDate.getDate()}`
      if (!messagesByDate[key]) messagesByDate[key] = { date: msgDate, messages: [] }
      messagesByDate[key].messages.push({ ...msg, originalIndex: index })
    })

    const today = new Date()
    const yesterday = new Date()
    yesterday.setDate(today.getDate() - 1)

    return Object.entries(messagesByDate).map(([key, group]) => {
      const label =
        group.date.toDateString() === today.toDateString()
          ? "Today"
          : group.date.toDateString() === yesterday.toDateString()
            ? "Yesterday"
            : group.date.toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric" })

      return (
        <div key={key} className="space-y-2">
          <div className="flex justify-center">
            <div className="bg-gray-200 text-gray-600 text-xs px-3 py-1 rounded-full">{label}</div>
          </div>
          {group.messages.map((msg) => renderMessageBubble(msg, msg.originalIndex))}
        </div>
      )
    })
  }

  const renderMessageBubble = (msg, messageIndex) => {
    const currentChat = conversations.find((c) => c._id === currentChatId)
    const isGroupChat = currentChat?.chatType === "pTe"

    return (
      <div key={msg._id} className={`flex ${msg.pid === currentUser?.pid ? "justify-end" : "justify-start"}`}>
        <div
          className={`message-bubble max-w-xs md:max-w-md lg:max-w-lg xl:max-w-xl rounded-lg p-3 relative flex flex-col ${
            msg.pid === currentUser?.pid ? "bg-blue-100 text-gray-800" : "bg-white text-gray-800"
          } ${selectedMessages.includes(msg._id) ? "message-selected" : ""}`}
          style={{
            borderTopLeftRadius: msg.pid !== currentUser?.pid ? "0" : undefined,
            borderTopRightRadius: msg.pid === currentUser?.pid ? "0" : undefined,
          }}
          onClick={(e) => {
            e.stopPropagation()
            if (isMobile) {
              handleMessageTap(msg)
            } else {
              handleMessageSelect(msg)
            }
          }}
          onTouchStart={() => handleTouchStart(msg)}
          onTouchEnd={handleTouchEnd}
        >
          {/* Desktop message selection menu */}
          {!isMobile && selectedMessage && selectedMessage._id === msg._id && (
            <div className="message-selection-menu">
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  handleReplyToMessage(msg)
                  clearMessageSelection()
                }}
                className="text-gray-600 hover:text-blue-500"
                title="Reply"
              >
                <Reply size={18} />
              </button>
              <button onClick={toggleReactionMenu} className="text-gray-600 hover:text-blue-500" title="React">
                <Smile size={18} />
              </button>
              {msg.pid === currentUser?.pid && (
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    if (window.confirm("Are you sure you want to delete this message?")) {
                      deleteMessage(msg._id)
                      clearMessageSelection()
                    }
                  }}
                  className="text-gray-600 hover:text-red-500"
                  title="Delete"
                >
                  <Trash2 size={18} />
                </button>
              )}
            </div>
          )}

          {/* Desktop reaction menu */}
          {!isMobile && selectedMessage && selectedMessage._id === msg._id && showReactionMenu && (
            <div className="reaction-menu" onClick={(e) => e.stopPropagation()}>
              <button onClick={() => addReaction("👍", msg._id)}>👍</button>
              <button onClick={() => addReaction("❤️", msg._id)}>❤️</button>
              <button onClick={() => addReaction("😂", msg._id)}>😂</button>
            </div>
          )}

          {/* Desktop hover action button */}
          {!isMobile && (
            <div className="message-actions hidden md:flex">
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  handleMessageSelect(msg)
                }}
                className="text-gray-500 hover:text-blue-500"
              >
                <MoreVertical size={16} />
              </button>
            </div>
          )}

          {/* Sender name for group chats */}
          {isGroupChat && msg.pid !== currentUser?.pid && (
            <div
              className="group-sender-name"
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                const participant = currentChat.participants.find((p) => p.pid === msg.pid)
                console.log("Group sender clicked:", participant)
                if (participant) {
                  handleProfileClick({
                    ...participant,
                    profileImageUrl: profileImageUrls[participant.pid] || null,
                  })
                }
              }}
              style={{ cursor: "pointer" }}
            >
              {msg.pName}
            </div>
          )}

          {/* Reply preview */}
          {msg.replyTo && renderReplyContent(msg.replyTo)}

          {/* Message content */}
          <div className="message-content break-words flex items-center">
            {msg.isDeleted ? (
              <p className="italic text-gray-500">This message was deleted</p>
            ) : msg.messageType === "file" ? (
              <div className="flex flex-col w-full">
                <div
                  className="flex items-center space-x-2 cursor-pointer mb-2"
                  onClick={async (e) => {
                    e.stopPropagation()
                    if (msg.mediaUrl) {
                      const presignedUrl = await generatePresignedUrl(msg.mediaUrl)
                      window.open(presignedUrl, "_blank")
                    }
                  }}
                >
                  {getFileIcon(msg.fileName || msg.message)}
                  <span className={`text-sm ${isMobile ? "mobile-file-name" : ""} truncate`}>
                    {msg.fileName || msg.message}
                  </span>
                </div>
                {/* Show caption if it's different from the filename */}
                {msg.fileName && msg.message !== msg.fileName && <p className="mt-1 text-gray-800">{msg.message}</p>}
              </div>
            ) : (
              <p>{msg.message}</p>
            )}
          </div>

          {/* Reactions */}
          {renderReactions(msg.reactions, msg._id)}

          {/* Timestamp & Status */}
          <div className="message-timestamp flex items-center justify-end mt-1 space-x-1">
            <span className="text-xs text-gray-500">
              {new Date(msg.createdAt).toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
              })}
            </span>
            {msg.pid === currentUser?.pid && renderMessageStatus(msg, messageIndex)}
          </div>
        </div>
      </div>
    )
  }

  // Function to handle message selection
  const handleMessageSelect = (message) => {
    setSelectedMessage(message)
    setShowReactionMenu(false)
  }

  // Function to cancel reply
  const cancelReply = () => {
    setReplyTo(null)
  }

  const handleSendMessage = async (e) => {
    e.preventDefault()

    if (ws && (message.trim() || file) && currentChatId && currentUser) {
      let chatId = currentChatId
      let isNewConversation = false

      if (chatId.startsWith("temp_")) {
        isNewConversation = true
        chatId = `new_${Date.now()}`
      }

      let newMessage

      if (file) {
        try {
          const s3Url = await postStudentChatFile(file, currentUser.pid, currentChatId, file.name)
          newMessage = {
            gid: currentChatId,
            pid: currentUser.pid,
            pName: currentUser.pName,
            messageType: "file",
            mediaUrl: s3Url,
            message: message.trim() || file.name, // Use message text as caption if available, otherwise use filename
            fileName: file.name, // Always store the filename separately
            replyTo: replyTo,
          }
          setFile(null)
          setSelectedFileName(null)
          if (fileInputRef.current) {
            fileInputRef.current.value = ""
          }
        } catch (error) {
          console.error("Error uploading file:", error)
          alert("Error uploading file")
          return
        }
      } else {
        newMessage = {
          gid: currentChatId,
          pid: currentUser.pid,
          pName: currentUser.pName,
          messageType: "text",
          mediaUrl: null,
          message: message.trim(),
          replyTo: replyTo,
        }
      }

      // Create local message for immediate display
      const localMessage = {
        ...newMessage,
        _id: `temp_msg_${Date.now()}`,
        createdAt: new Date().toISOString(),
        reactions: [],
        isLocalMessage: true, // Flag to identify local messages
      }

      // Add message to local state immediately for better UX
      setCurrentMessageArray((prevMessages) => {
        const newMessages = [...prevMessages, localMessage]
        return newMessages
      })

      if (isNewConversation) {
        const currentChat = conversations.find((c) => c._id === currentChatId)

        const newConversationMessage = {
          action: "setGroup",
          chatType: "pTp",
          participants: [
            { pid: currentUser.pid, pName: currentUser.pName, unseenCounter: 0 },
            ...(currentChat?.participants.filter((p) => p.pid !== currentUser.pid) || []),
          ],
          message: {
            gid: chatId,
            pid: currentUser.pid,
            pName: currentUser.pName,
            messageType: file ? "file" : "text",
            mediaUrl: file ? newMessage.mediaUrl : null,
            message: file ? file.name : message.trim(),
            replyTo: replyTo,
          },
        }
        ws.send(JSON.stringify(newConversationMessage))

        // Update the temporary conversation with the message
        setConversations((prevConversations) => {
          return prevConversations.map((chat) => {
            if (chat._id === currentChatId) {
              return {
                ...chat,
                latestMessage: newMessage.message,
                latestMessageTime: new Date().toISOString(),
                latestMessageTimeFormatted: formatMessageTimeForDisplay(new Date()),
                latestMessageSendBy: {
                  pid: currentUser.pid,
                  pName: currentUser.pName,
                },
                participants: chat.participants?.map((p) => {
                  if (p.pid === currentUser.pid) {
                    return { ...p, seen: "y", unseenCounter: 0 }
                  } else {
                    return { ...p, seen: "n", unseenCounter: 1 } // Grey checkmarks for new conversation
                  }
                }),
              }
            }
            return chat
          })
        })
      } else {
        ws.send(
          JSON.stringify({
            action: "sendMessage",
            ...newMessage,
            gid: chatId,
          }),
        )

        // Update existing conversation
        setConversations((prevConversations) => {
          const updatedChat = prevConversations.find((chat) => chat._id === chatId)
          if (!updatedChat) return prevConversations

          const updatedChatWithMessage = {
            ...updatedChat,
            latestMessage: newMessage.message,
            latestMessageTime: new Date().toISOString(),
            latestMessageTimeFormatted: formatMessageTimeForDisplay(new Date()),
            latestMessageSendBy: {
              pid: currentUser.pid,
              pName: currentUser.pName,
            },
            participants: updatedChat.participants?.map((p) => {
              if (p.pid === currentUser.pid) {
                return { ...p, seen: "y", unseenCounter: 0 }
              } else {
                return { ...p, seen: "n", unseenCounter: (p.unseenCounter || 0) + 1 }
              }
            }),
          }

          const filteredConversations = prevConversations.filter((chat) => chat._id !== chatId)
          return [updatedChatWithMessage, ...filteredConversations]
        })
      }

      setIsTyping(false)
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current)
      }

      setReplyTo(null)
      setMessage("")
      setFile(null)
      setSelectedFileName(null)
      // NEW: Clear mention dropdown
      setShowMentionDropdown(false)
      if (fileInputRef.current) {
        fileInputRef.current.value = ""
      }
    }
  }

  // Enhanced long press handlers for mobile
  const handleTouchStart = (message) => {
    if (!isMobile) return

    const timer = setTimeout(() => {
      setIsSelectionMode(true)
      setSelectedMessages([message._id])
      setShowMobileEmojiBar(true)

      if (navigator.vibrate) {
        navigator.vibrate(50)
      }
    }, 500)

    setLongPressTimer(timer)
  }

  const handleTouchEnd = () => {
    if (longPressTimer) {
      clearTimeout(longPressTimer)
      setLongPressTimer(null)
    }
  }

  // Enhanced message selection handlers for mobile
  const handleMessageTap = (message) => {
    if (!isMobile) return

    if (isSelectionMode) {
      setSelectedMessages((prev) => {
        if (prev.includes(message._id)) {
          const newSelection = prev.filter((id) => id !== message._id)
          if (newSelection.length === 0) {
            setIsSelectionMode(false)
            setShowMobileEmojiBar(false)
          }
          return newSelection
        } else {
          return [...prev, message._id]
        }
      })
    }
  }

  const truncateText = (text, maxLength) => {
    if (!text) return ""
    if (text?.length <= maxLength) return text
    return text?.slice(0, maxLength) + "..."
  }

  const removeSelectedFile = () => {
    setFile(null)
    setSelectedFileName(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ""
    }
  }

  // Helper function to format typing indicators
  const formatTypingIndicator = (typingUsers) => {
    if (!typingUsers || typingUsers.length === 0) return null

    if (typingUsers.length === 1) {
      return `${typingUsers[0].pName} is typing`
    } else if (typingUsers.length === 2) {
      return `${typingUsers[0].pName} and ${typingUsers[1].pName} are typing`
    } else {
      return `${typingUsers.length} people are typing`
    }
  }

  // Helper function to render reply content
  const renderReplyContent = (replyData) => {
    if (!replyData) return null

    return (
      <div className="reply-container">
        <div className="reply-header">
          <CornerUpLeft size={14} className="text-blue-500 mr-1" />
          <span className="text-xs font-semibold text-blue-600">{replyData.pName}</span>
        </div>
        <div className="reply-content">
          {replyData.messageType === "file" ? (
            <div className="flex items-center">
              {getFileIcon(replyData.message)}
              <span className="text-xs ml-1">{truncateText(replyData.message, 30)}</span>
            </div>
          ) : (
            truncateText(replyData.message, 50)
          )}
        </div>
      </div>
    )
  }

  // Function to clear selection
  const clearSelection = () => {
    setIsSelectionMode(false)
    setSelectedMessages([])
    setShowMobileEmojiBar(false)
  }

  // FIXED: Handle WebSocket messages for current chat
  useEffect(() => {
    if (!ws) return

    const handleWebSocketMessage = (event) => {
      try {
        const data = JSON.parse(event.data)

        // Handle the nested message structure from WebSocket
        const messageData = data.message || data

        if (messageData && messageData.action === "sendMessage") {
          const {
            gid,
            pid,
            pName,
            messageType,
            mediaUrl,
            message,
            replyTo,
            targetMessageId,
            reaction,
            deletedMessageId,
            createdAt,
            _id,
          } = messageData

          // Handle message deletion
          if (messageType === "delete" && deletedMessageId) {
            if (currentChatId === gid) {
              setCurrentMessageArray((prevMessages) => {
                return prevMessages.map((msg) => {
                  if (msg._id === deletedMessageId) {
                    return {
                      ...msg,
                      isDeleted: true,
                      message: "This message was deleted",
                    }
                  }
                  return msg
                })
              })
            }
            return
          }

          // FIXED: Handle reaction message type
          if (messageType === "reaction" && targetMessageId) {
            if (currentChatId === gid) {
              setCurrentMessageArray((prevMessages) => {
                return prevMessages.map((msg) => {
                  if (msg._id === targetMessageId) {
                    const existingReactionIndex = msg.reactions?.findIndex(
                      (r) => r.pid === pid && r.reaction === reaction,
                    )

                    const updatedReactions = [...(msg.reactions || [])]

                    if (existingReactionIndex !== undefined && existingReactionIndex >= 0) {
                      // Remove existing reaction (toggle off)
                      updatedReactions.splice(existingReactionIndex, 1)
                    } else {
                      // Add new reaction
                      updatedReactions.push({
                        pid,
                        pname: pName,
                        pName,
                        reaction,
                        _id: `reaction_${Date.now()}_${Math.random()}`,
                      })
                    }

                    return {
                      ...msg,
                      reactions: updatedReactions,
                    }
                  }
                  return msg
                })
              })
            }
            return
          }

          // Handle regular text/file messages
          if ((messageType === "text" || messageType === "file") && message) {
            // Clear typing indicator when a message is received
            if (typingStatus[gid] && typingStatus[gid][pid]) {
              setTypingStatus((prev) => {
                const newStatus = { ...prev }
                if (newStatus[gid]) {
                  const newGidStatus = { ...newStatus[gid] }
                  delete newGidStatus[pid]
                  newStatus[gid] = newGidStatus
                }
                return newStatus
              })
            }

            // Send seen status if we're currently viewing this chat and message is from someone else
            if (gid === currentChatId && currentUser && pid !== currentUser.pid) {
              setSeenViaAPI(gid, currentUser.pid)

              setConversations((prevConversations) =>
                prevConversations.map((chat) =>
                  chat._id === gid
                    ? {
                        ...chat,
                        participants: chat.participants?.map((p) =>
                          p.pid === currentUser.pid ? { ...p, seen: "y", unseenCounter: 0 } : p,
                        ),
                      }
                    : chat,
                ),
              )
            }

            // Show notification if message is from someone else and we're not viewing that chat
            if (pid !== currentUser?.pid && gid !== currentChatId) {
              if (Notification.permission === "granted") {
                new Notification(`New message from ${pName}`, {
                  body: messageType === "text" ? message : "Sent a file",
                  icon: "/notification-icon.png",
                })
              }
            }

            // Add message to current chat if we're viewing it
            if (currentChatId === gid) {
              setCurrentMessageArray((prevMessages) => {
                // Check if message already exists (avoid duplicates)
                const messageExists = prevMessages.some(
                  (msg) =>
                    msg._id === _id ||
                    (msg.isLocalMessage &&
                      msg.pid === pid &&
                      msg.message === message &&
                      Math.abs(new Date(msg.createdAt).getTime() - new Date(createdAt || new Date()).getTime()) < 5000),
                )

                if (messageExists) {
                  // If it's a local message being replaced by the real one, update it
                  return prevMessages.map((msg) => {
                    if (
                      msg.isLocalMessage &&
                      msg.pid === pid &&
                      msg.message === message &&
                      Math.abs(new Date(msg.createdAt).getTime() - new Date(createdAt || new Date()).getTime()) < 5000
                    ) {
                      return {
                        ...msg,
                        _id: _id || msg._id,
                        createdAt: createdAt || msg.createdAt,
                        isLocalMessage: false,
                      }
                    }
                    return msg
                  })
                }

                // Add new message
                const newMessage = {
                  _id: _id || `ws_msg_${Date.now()}_${Math.random()}`,
                  pid,
                  pName,
                  messageType,
                  mediaUrl,
                  message,
                  replyTo,
                  createdAt: createdAt || new Date().toISOString(),
                  reactions: [],
                  gid,
                }

                return [...prevMessages, newMessage]
              })

              // Auto-scroll to bottom for new messages (only if user is near bottom)
              setTimeout(() => {
                const container = messagesContainerRef.current
                if (container) {
                  const isNearBottom = container.scrollTop + container.clientHeight >= container.scrollHeight - 100
                  if (isNearBottom) {
                    scrollToBottom()
                  }
                }
              }, 100)
            }
          }
        }
      } catch (error) {
        console.error("Error parsing WebSocket message:", error)
      }
    }

    ws.addEventListener("message", handleWebSocketMessage)

    return () => {
      ws.removeEventListener("message", handleWebSocketMessage)
    }
  }, [currentChatId, currentUser, ws, typingStatus, setTypingStatus, setSeenViaAPI, setConversations])

  if (!currentChatId) {
    return (
      <div className={`md:flex-1 flex items-center justify-center bg-white ${isMobile ? "h-full" : "h-screen"}`}>
        {!isMobile && (
          <div className="text-center">
            <MessageSquare size={64} className="mx-auto text-gray-400 mb-4" />
            <h2 className="text-xl font-semibold text-gray-600 mb-2">Welcome to ChitChat</h2>
            <p className="text-gray-500">Select a conversation to start chatting</p>
          </div>
        )}
      </div>
    )
  }

  return (
    <div
      className={`md:flex-1 flex flex-col bg-white ${
        isMobile && !isMobileChatOpen ? "hidden" : "block"
      } ${isMobile && isMobileChatOpen ? "fixed inset-0 z-50" : "h-screen"} w-full mobile-chat-view ${isMobile ? "" : "pb-2"} ${isSelectionMode && isMobile ? "mobile-selection-mode" : ""}`}
    >
      <style jsx>{chatAreaStyles}</style>

      {/* Mobile Selection Header - WhatsApp style */}
      {isMobile && isSelectionMode && (
        <>
          <div className="mobile-chat-overlay" onClick={clearSelection} />
          <div className="mobile-selection-header">
            <div className="flex items-center">
              <button onClick={clearSelection} className="mr-4">
                <X size={20} />
              </button>
              <span className="font-medium">{selectedMessages.length} selected</span>
            </div>
            <div className="mobile-selection-actions">
              {selectedMessages.length === 1 && (
                <button
                  onClick={() => {
                    const message = currentMessageArray.find((m) => m._id === selectedMessages[0])
                    if (message) {
                      handleReplyToMessage(message)
                      clearSelection()
                    }
                  }}
                  className="p-2 rounded-full hover:bg-blue-600"
                >
                  <Reply size={20} />
                </button>
              )}
              <button onClick={deleteSelectedMessages} className="p-2 rounded-full hover:bg-blue-600">
                <Trash2 size={20} />
              </button>
            </div>
          </div>
        </>
      )}

      {/* Mobile Emoji Bar - WhatsApp style */}
      {isMobile && isSelectionMode && showMobileEmojiBar && (
        <div className="mobile-selection-emoji-bar">
          <button onClick={() => addReactionToSelected("👍")}>👍</button>
          <button onClick={() => addReactionToSelected("❤️")}>❤️</button>
          <button onClick={() => addReactionToSelected("😂")}>😂</button>
          <button onClick={() => addReactionToSelected("😢")}>😢</button>
          <button onClick={() => addReactionToSelected("🙏")}>🙏</button>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between px-4 py-6 bg-white text-gray-800 border-b border-gray-200 sticky top-0 z-10 w-full chat-header shadow-sm h-20 flex-shrink-0">
        <div className="flex items-center">
          <button
            className="md:hidden mr-3 text-gray-800"
            onClick={() => {
              if (isMobile) {
                setIsMobileChatOpen(false)
                setCurrentChatId(null)
                setCurrentMessageArray([])
              }
              clearSelection()
            }}
          >
            <ArrowLeft size={24} />
          </button>
          {renderChatHeader()}
        </div>
        <div className="flex items-center space-x-4">{renderGroupInfoButton()}</div>
      </div>

      {/* Chat messages */}
      <div
        ref={messagesContainerRef}
        className={`flex-1 overflow-y-auto p-4 space-y-2 bg-gray-50 custom-scrollbar w-full chat-messages ${isMobile ? "mobile-chat-messages" : ""}`}
        style={{
          height: isMobile ? "calc(100vh - 180px)" : "calc(100vh - 240px)",
          maxHeight: isMobile ? "calc(100vh - 180px)" : "calc(100vh - 240px)",
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
        }}
        onClick={() => !isSelectionMode && clearMessageSelection()}
      >
        {isLoadingMoreMessages && (
          <div className="flex justify-center items-center py-4">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
            <span className="ml-2 text-sm text-gray-500">Loading more messages...</span>
          </div>
        )}

        {hasMoreMessages && !isLoadingMoreMessages && currentMessageArray.length >= 100 && (
          <div className="flex justify-center items-center py-2">
            <button onClick={loadMoreMessages} className="text-sm text-blue-500 hover:text-blue-700 focus:outline-none">
              Load earlier messages
            </button>
          </div>
        )}

        {isLoadingMessages ? (
          <div className="flex justify-center items-center h-full">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          </div>
        ) : currentMessageArray.length > 0 ? (
          renderGroupedMessages()
        ) : (
          <div className="p-4 text-center text-gray-500">No messages yet.</div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Typing Indicator */}
      <div className="typing-indicator px-4 py-1 bg-white border-t border-gray-100 flex-shrink-0">
        {renderTypingIndicator()}
      </div>

      {/* Reply Preview */}
      {replyTo && (
        <div className="bg-gray-50 px-4 py-2 border-t border-gray-200 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <CornerUpLeft size={16} className="text-blue-500 mr-2" />
              <div>
                <div className="text-xs font-semibold text-blue-600">Replying to {replyTo.pName}</div>
                <div className="text-xs text-gray-500">
                  {replyTo.messageType === "file" ? (
                    <div className="flex items-center">
                      {getFileIcon(replyTo.message)}
                      <span className="ml-1">{truncateText(replyTo.message, 30)}</span>
                    </div>
                  ) : (
                    truncateText(replyTo.message, 50)
                  )}
                </div>
              </div>
            </div>
            <button onClick={cancelReply} className="text-gray-500 hover:text-gray-700">
              <X size={16} />
            </button>
          </div>
        </div>
      )}

      {/* File preview */}
      {selectedFileName && (
        <div className="bg-gray-50 px-4 py-2 border-t border-gray-200 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              {getFileIcon(selectedFileName)}
              <span className={`ml-2 text-sm text-gray-700 ${isMobile ? "mobile-file-name" : ""}`}>
                {selectedFileName}
              </span>
            </div>
            <button onClick={removeSelectedFile} className="text-gray-500 hover:text-gray-700">
              <X size={16} />
            </button>
          </div>
      
        </div>
      )}

      {/* Message input */}
      <form
        onSubmit={handleSendMessage}
        className={`flex items-center p-4 bg-white border-t border-gray-200 flex-shrink-0 w-full ${isMobile ? "" : "mb-2"}`}
      >
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          accept=".pdf,.jpg,.jpeg,.png,.gif"
          className="hidden"
        />
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          className="mr-2 p-2 text-gray-500 hover:text-gray-700 focus:outline-none"
        >
          <FileIcon size={20} />
        </button>
        <div className="relative flex-1">
          {/* Mention dropdown */}
          <MentionDropdown />
          <input
            type="text"
            value={message}
            onChange={handleTyping}
            onKeyDown={handleMentionKeyDown}
            ref={messageInputRef}
            placeholder="Type a message..."
            className="w-full px-4 py-2 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <button
          type="submit"
          className="ml-2 p-2 bg-blue-500 text-white rounded-full hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 disabled:opacity-50"
          disabled={!message.trim() && !file}
        >
          <Send size={20} />
        </button>
      </form>

      {/* Reaction Details Modal */}
      <ReactionDetailsModal />
    </div>
  )
}

export default ChatArea
